// Authenticated fetch wrapper for the dashboard.
//
// Verbatim port of apps/web-svelte/src/lib/api/client.ts with two
// adaptations: (1) the session-expired redirect is installed as a
// React component effect via SessionExpiryHandler instead of a goto()
// call from a module; (2) the session store lives in
// ./sessionStore.ts (plain TS) instead of a Svelte writable. The
// dual-refresh ladder, the in-flight coalesce, and the error
// normalization are unchanged — bug fixes to either client.ts must
// land in both.
//
// Responsibilities (and only these — no domain logic):
//   1. Inject Authorization: Bearer <accessToken> when a session exists.
//   2. Normalize the v3 error envelope into ApiError.
//   3. Refresh access tokens transparently — proactively when within
//      30s of expiry, reactively on the first 401 (one retry, no loops).
//   4. On unrecoverable auth failure: clear the session and dispatch
//      bp:session-expired. SessionExpiryHandler routes to /login.

import { AUTH_BASE, baseFor, type ApiBase } from "./bases";
import { ApiError, fromHttpResponse, fromNetworkError } from "./error";
import { isAccessExpiring, type Session } from "./session";
import { clearAuth, currentSession, setSession } from "./sessionStore";

export type RequestOptions = {
  body?: unknown;
  headers?: Record<string, string>;
  // Set true for endpoints the server treats as public (login,
  // refresh). Skips auth-header injection AND the 401-refresh ladder
  // — a 401 from a public endpoint is a real error to surface.
  skipAuth?: boolean;
  signal?: AbortSignal;
};

let refreshInFlight: Promise<Session> | null = null;

async function refreshTokens(currentRefreshToken: string): Promise<Session> {
  const res = await fetch(`${AUTH_BASE}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken: currentRefreshToken }),
  });
  if (!res.ok) {
    let body: unknown = undefined;
    try {
      body = await res.json();
    } catch {
      /* keep body undefined */
    }
    throw fromHttpResponse(res.status, body);
  }
  const next = (await res.json()) as Session;
  setSession(next);
  return next;
}

async function ensureFreshSession(): Promise<Session | null> {
  const session = currentSession();
  if (!session) return null;
  if (!isAccessExpiring(session)) return session;
  if (!refreshInFlight) {
    refreshInFlight = refreshTokens(session.refreshToken).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

function buildHeaders(
  session: Session | null,
  init: RequestOptions,
  hasJsonBody: boolean
): Headers {
  const h = new Headers(init.headers ?? {});
  // Only set Content-Type for JSON bodies — when init.body is a
  // FormData instance, the browser must set Content-Type itself so
  // it can include the correct multipart boundary.
  if (hasJsonBody && !h.has("Content-Type")) {
    h.set("Content-Type", "application/json");
  }
  if (!init.skipAuth && session) {
    h.set("Authorization", `Bearer ${session.accessToken}`);
  }
  return h;
}

async function readBody(res: Response): Promise<unknown> {
  if (res.status === 204) return null;
  const ct = res.headers.get("content-type") ?? "";
  if (!ct.includes("application/json")) {
    if (!res.ok) return undefined;
    return null;
  }
  try {
    return await res.json();
  } catch (err) {
    if (!res.ok) throw fromHttpResponse(res.status, undefined);
    throw new ApiError({
      status: res.status,
      code: "PARSE_ERROR",
      message: err instanceof Error ? err.message : "Failed to parse response",
    });
  }
}

async function rawRequest<T>(
  method: string,
  base: ApiBase,
  path: string,
  init: RequestOptions,
  session: Session | null
): Promise<T> {
  const hasBody = init.body !== undefined && init.body !== null;
  // FormData is passed through verbatim and the multipart boundary is
  // set by the browser. JSON bodies are serialized inline. The
  // Content-Type header is suppressed for FormData (see buildHeaders).
  const isFormData = typeof FormData !== "undefined" && init.body instanceof FormData;
  const headers = buildHeaders(session, init, hasBody && !isFormData);
  const fetchInit: RequestInit = { method, headers };
  if (hasBody) {
    fetchInit.body = isFormData ? (init.body as FormData) : JSON.stringify(init.body);
  }
  if (init.signal) fetchInit.signal = init.signal;
  let res: Response;
  try {
    res = await fetch(`${baseFor(base)}${path}`, fetchInit);
  } catch (err) {
    throw fromNetworkError(err);
  }
  const body = await readBody(res);
  if (!res.ok) throw fromHttpResponse(res.status, body);
  return body as T;
}

export async function request<T>(
  method: string,
  base: ApiBase,
  path: string,
  init: RequestOptions = {}
): Promise<T> {
  if (init.skipAuth) {
    return rawRequest<T>(method, base, path, init, null);
  }

  const session = await tryEnsureFresh();
  try {
    return await rawRequest<T>(method, base, path, init, session);
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) throw err;
    const refreshed = await tryRefreshOrFail();
    if (!refreshed) throw err;
    return rawRequest<T>(method, base, path, init, refreshed);
  }
}

// Blob-returning variant for binary downloads (xlsx, pdf, etc.).
// readBody() above short-circuits non-JSON bodies to null, so request<Blob>
// would silently return null instead of the binary payload.
export type BlobDownload = {
  blob: Blob;
  /** The server's `Content-Disposition` filename, null when absent. */
  filename: string | null;
};

/** `attachment; filename="x.xlsx"` → `x.xlsx` (quoted or bare token). */
export function contentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const m = /filename\s*=\s*(?:"([^"]+)"|([^;\s]+))/i.exec(header);
  const name = (m?.[1] ?? m?.[2] ?? "").trim();
  // A filename is a basename: anything carrying a path is ignored.
  return name.length > 0 && !/[\\/]/.test(name) ? name : null;
}

async function rawRequestBlob(
  method: string,
  base: ApiBase,
  path: string,
  init: RequestOptions,
  session: Session | null
): Promise<BlobDownload> {
  const headers = buildHeaders(session, init, false);
  const fetchInit: RequestInit = { method, headers };
  if (init.signal) fetchInit.signal = init.signal;
  let res: Response;
  try {
    res = await fetch(`${baseFor(base)}${path}`, fetchInit);
  } catch (err) {
    throw fromNetworkError(err);
  }
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = undefined;
    }
    throw fromHttpResponse(res.status, body);
  }
  return {
    blob: await res.blob(),
    filename: contentDispositionFilename(res.headers.get("Content-Disposition")),
  };
}

export async function requestBlob(
  method: string,
  base: ApiBase,
  path: string,
  init: RequestOptions = {}
): Promise<Blob> {
  return (await requestDownload(method, base, path, init)).blob;
}

/** requestBlob plus the server-chosen filename (Content-Disposition). */
export async function requestDownload(
  method: string,
  base: ApiBase,
  path: string,
  init: RequestOptions = {}
): Promise<BlobDownload> {
  if (init.skipAuth) {
    return rawRequestBlob(method, base, path, init, null);
  }

  const session = await tryEnsureFresh();
  try {
    return await rawRequestBlob(method, base, path, init, session);
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) throw err;
    const refreshed = await tryRefreshOrFail();
    if (!refreshed) throw err;
    return rawRequestBlob(method, base, path, init, refreshed);
  }
}

async function tryEnsureFresh(): Promise<Session | null> {
  try {
    return await ensureFreshSession();
  } catch (err) {
    handleSessionExpired();
    if (err instanceof ApiError) throw err;
    throw fromNetworkError(err);
  }
}

async function tryRefreshOrFail(): Promise<Session | null> {
  const session = currentSession();
  if (!session) return null;
  try {
    if (!refreshInFlight) {
      refreshInFlight = refreshTokens(session.refreshToken).finally(() => {
        refreshInFlight = null;
      });
    }
    return await refreshInFlight;
  } catch {
    handleSessionExpired();
    return null;
  }
}

function handleSessionExpired(): void {
  clearAuth();
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("bp:session-expired"));
}
